import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import axios from "axios";
import SubmitButton from "common/components/Button/SubmitButton";
import CustomBreadcrumb from "common/components/CustomBreadcrumb";
import LoadingSpinner from "common/components/LoadingSpinner";
import ProjectForm from "common/containers/Forms/Project/ProjectForm";
import { useAuth } from "common/hooks/useAuth";
import { BACKEND_URL_CONFIG } from "config";
import { ERRORS } from "errors";
import MainLayout from "layouts/MainLayout";
import { Fragment, useEffect, useId, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./EditProjectPage.module.css";

export default function EditProjectPage() {
  // Contexts
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // States
  const [loading, setLoading] = useState(false);
  const [allUsersInformation, setAllUsersInformation] = useState({});
  const [formData, setFormData] = useState(null);
  const [projectObject, setProjectObject] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { projectId } = useParams();

  // Form states
  const formId = useId();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [projectInfo, usersInfo] = await Promise.all([
          fetchProjectInformation(),
          fetchUsersInformation(),
        ]);
        setProjectObject(projectInfo);
        setAllUsersInformation(usersInfo);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (formData) {
      const payload = {
        name: formData.name,
        user_id: currentUser.uid,
        available_credits: parseFloat(formData.available_credits),
        owner_ids: formData.owner_ids,
        authorized_user_ids: formData.authorized_user_ids,
      };
      updateProject(payload);
    }
  }, [formData]);

  const fetchProjectInformation = async () => {
    try {
      const url = BACKEND_URL_CONFIG.getProjectInformation.replace(
        "{projectId}",
        projectId
      );
      const response = await axios.get(url, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
      });
      return response.data;
    } catch (error) {
      throw Error(ERRORS.error_141);
    }
  };
  const fetchUsersInformation = async () => {
    try {
      const response = await axios.get(BACKEND_URL_CONFIG.getAllUserNames, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
      });
      return response.data;
    } catch (error) {
      throw Error(ERRORS.error_133);
    }
  };

  const updateProject = async (payload) => {
    const url = BACKEND_URL_CONFIG.updateProject.replace(
      "{projectId}",
      projectId
    );
    try {
      setLoading(true);
      const response = await axios.put(url, payload, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
      });
      if (response?.status === 200) {
        toast.success("Le projet a été modifié.");
        navigate("/projects");
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error(
          "Ce nom est utilisé pour un autre projet. Veuillez choisir un autre."
        );
        return;
      }
      toast.error(ERRORS.error_140);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <MainLayout>
        <div className={styles.container}>
          <div className={styles["breadcrumbs-wrapper"]}>
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <CustomBreadcrumb
                component="a"
                href="/"
                label="Page d'accueil"
                icon={<FontAwesomeIcon icon={faHome} />}
              />
              <CustomBreadcrumb
                component="a"
                href="/projects"
                label="Projets"
              />
              <CustomBreadcrumb label="Editer un projet" href="#" />
            </Breadcrumbs>
          </div>
          <div className={styles["form-container"]}>
            <div className={styles["title-wrapper"]}>
              <div className={styles.title}>Editer le projet</div>
            </div>
            <div className={styles["form-wrapper"]}>
              <div className={styles["content"]}>
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  <ProjectForm
                    formId={formId}
                    projectObject={projectObject}
                    setFormData={setFormData}
                    allUsersInformation={allUsersInformation}
                    setIsSubmitting={setIsSubmitting}
                  />
                )}
              </div>
            </div>
            <div className={styles["stepper-action-buttons-wrapper"]}>
              <SubmitButton
                disabled={isSubmitting}
                style={{ backgroundColor: "#142637cc", color: "white" }}
                formId={formId}
                value="Enregistrer"
              />
            </div>
          </div>
        </div>
      </MainLayout>
    </Fragment>
  );
}
